import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CookieBanner } from './CookieBanner';

interface MarketingShellProps {
  children: ReactNode;
}

// Shared wrapper for the marketing-side routes: /, /privacy, /terms, /support.
// Same nav and footer everywhere so the site feels cohesive.
export function MarketingShell({ children }: MarketingShellProps) {
  return (
    <div className="min-h-full bg-cream text-ink">
      <header className="border-b border-ink/5 bg-cream/80 backdrop-blur sticky top-0 z-10">
        <nav className="max-w-5xl mx-auto px-6 pt-3 pb-2 sm:pt-4 sm:pb-3">
          {/* Top row: brand mark + name on the left, nav links + pill CTA on the right.
              The strapline is OUT of this row so the brand text and pill CTA both have
              full width and the pill stays a clean pill, not a squeezed circle. */}
          <div className="flex items-center justify-between gap-3">
            <Link to="/" className="flex items-center gap-2.5 group" aria-label="My Dyslexia Tools home">
              <svg
                className="w-9 h-9 flex-shrink-0"
                viewBox="0 0 64 64"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="58" height="58" rx="16" fill="#0369a1" />
                <circle cx="45" cy="19" r="7.5" fill="#FAFAF8" />
              </svg>
              <span className="font-semibold tracking-tight text-ink whitespace-nowrap">
                My Dyslexia Tools
              </span>
            </Link>
            <ul className="flex items-center gap-5 sm:gap-6 text-sm text-ink/70 flex-shrink-0">
              <li className="hidden md:block">
                <Link className="hover:text-ink transition" to="/schools">
                  Schools
                </Link>
              </li>
              <li className="hidden sm:block">
                <Link className="hover:text-ink transition" to="/support">
                  Support
                </Link>
              </li>
              <li>
                <Link
                  className="inline-flex items-center whitespace-nowrap bg-accent text-white px-4 py-2 rounded-full font-medium hover:brightness-105 transition"
                  to="/app"
                >
                  Start free
                </Link>
              </li>
            </ul>
          </div>
          {/* Strapline runs full-width below the top row on every breakpoint. Uses the
              same uppercase tracked-out treatment as the eyebrow on each marketing
              section. Font size scales with viewport so the line always fits without
              wrapping, even on a 320px iPhone SE. */}
          <p
            className="uppercase tracking-[0.08em] sm:tracking-[0.22em] text-ink/45 mt-2 sm:mt-1.5 leading-tight whitespace-nowrap overflow-hidden"
            style={{ fontSize: 'clamp(7.5px, 2.4vw, 10px)' }}
          >
            Lifting the barrier to learning, one tool at a time
          </p>
        </nav>
      </header>

      <main>{children}</main>

      <footer className="border-t border-ink/5 mt-12 bg-white/60">
        <div className="max-w-5xl mx-auto px-6 py-10 grid gap-6 sm:grid-cols-4 text-sm text-ink/70">
          <div>
            <p className="font-semibold text-ink mb-2">My Dyslexia Tools</p>
            <p>Built by a parent of a child with dyslexia.</p>
            <p className="mt-2 text-xs text-ink/50">A Creative Sauce Ltd product.</p>
          </div>
          <div>
            <p className="font-semibold text-ink mb-2">Product</p>
            <ul className="space-y-1">
              <li>
                <Link className="hover:text-ink" to="/app">
                  Answer Assist
                </Link>
              </li>
              <li>
                <span className="text-ink/40">Read To Me (coming soon)</span>
              </li>
              <li>
                <span className="text-ink/40">Word Bank (coming soon)</span>
              </li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-ink mb-2">Schools</p>
            <ul className="space-y-1">
              <li>
                <Link className="hover:text-ink" to="/schools">
                  School licence
                </Link>
              </li>
              <li>
                <Link className="hover:text-ink" to="/exam-mode">
                  Exam Mode policy
                </Link>
              </li>
              <li>
                <a
                  className="hover:text-ink"
                  href="mailto:support@mydyslexiatools.com?subject=School%20pilot%20-%20My%20Dyslexia%20Tools"
                >
                  Pilot enquiries
                </a>
              </li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-ink mb-2">Help</p>
            <ul className="space-y-1">
              <li>
                <Link className="hover:text-ink" to="/support">
                  Support
                </Link>
              </li>
              <li>
                <Link className="hover:text-ink" to="/privacy">
                  Privacy
                </Link>
              </li>
              <li>
                <Link className="hover:text-ink" to="/terms">
                  Terms
                </Link>
              </li>
              <li>
                <Link className="hover:text-ink" to="/ai-policy">
                  AI Policy
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-ink/5 py-4 text-center text-xs text-ink/40">
          (c) {new Date().getFullYear()} Creative Sauce Ltd. All rights reserved.
        </div>
      </footer>
      <CookieBanner />
    </div>
  );
}
